import React, { useEffect, useState } from "react";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import SearchIcon from "@mui/icons-material/Search";
import { LicenseTranslation } from "../../Translations/LicenseTranslation";
import { Language, License } from "../../Types/types";
import { useLanguage } from "../../components/LanguageContext";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GeneralTranslation } from "../../Translations/GeneralTranslation";
import { MdEdit } from "react-icons/md";
import { FaTrash } from "react-icons/fa";
import { useSelector } from "react-redux";
import { RootState } from "../../components/Manual_List/fetch/store";
import CircularProgress from "@mui/material/CircularProgress";
import { ConfirmationModal } from "../../components/shared/ConfrimationModal";
import {
  deleteLicense,
  fetchLicenses,
} from "../../components/Manual_List/fetch/slices";
import { useAppDispatch } from "../../components/Manual_List/fetch/hook";
import { useNavigate } from "react-router-dom";

const LicensesList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const licenses: License[] = useSelector(
    (state: RootState) => state.data.licenses.data
  );

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [licenseToDelete, setLicenseToDelete] = useState<License | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const { language } = useLanguage();
  const gt = GeneralTranslation[language as Language];
  const t = LicenseTranslation[language as Language];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  useEffect(() => {
    setLoading(true);
    dispatch(fetchLicenses());
    setLoading(false);
  }, [dispatch]);

  const openConfirmationModal = (license: License) => {
    setLicenseToDelete(license);
    setIsConfirmationModalOpen(true);
  };

  const handleDeleteLicense = (id: number) => {
    dispatch(deleteLicense(id));
    setIsConfirmationModalOpen(false);
  };

  const filteredLicenses = licenses.filter((license) => {
    return (
      searchTerm === "" ||
      license.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      license.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const datetimeFormat = (datetimeString: any) => {
    if (!datetimeString) return "";

    const date = new Date(datetimeString.date);
    return date.toLocaleString();
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />

      <div className="flex flex-col items-center justify-start flex-grow relative">
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-9xl flex flex-col">
          <div
            className={`flex ${
              isSmallScreen || isMediumScreen
                ? "flex-col items-center"
                : "justify-between"
            } items-center mb-4`}
          >
            <h1
              className={`${
                isSmallScreen ? "text-lg" : "text-xl"
              } font-semibold`}
            >
              {t.title}
            </h1>
            <div
              className={`flex ${
                isSmallScreen || isMediumScreen
                  ? "flex-col space-y-2 items-center w-full"
                  : "space-x-4"
              }`}
            >
              <div className="relative w-64">
                <input
                  type="text"
                  placeholder={gt.search}
                  className="px-3 py-2 border border-purple rounded-md w-full pr-10 outline-none dark:bg-darkBackground"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.currentTarget.value)}
                />
                <SearchIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 dark:text-white " />
              </div>
            </div>
          </div>
          <div
            className={`flex ${
              isSmallScreen || isMediumScreen
                ? "justify-center mb-4"
                : "justify-end mb-4"
            }`}
          >
            <button
              className="bg-purple text-white px-4 py-2 rounded font-medium shadow-sm"
              onClick={() => navigate("/add-license")}
            >
              {t.addLicense}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-4 font-medium text-left">{gt.name}</th>
                  <th className="py-2 px-4 font-medium text-left">{t.email}</th>
                  <th className="py-2 px-4 font-medium text-left">
                    {t.numberOfUsers}
                  </th>
                  <th className="py-2 px-4 font-medium text-left">
                    {t.createdAt}
                  </th>
                  <th className="py-2 px-4 font-medium text-left">
                    {t.validUntil}
                  </th>
                  <th className="py-2 px-4 font-medium text-left"></th>
                </tr>
              </thead>
              <tbody>
                {filteredLicenses.map((license, index) => (
                  <tr
                    key={license.id}
                    className={`${
                      index !== licenses.length - 1
                        ? "border-b border-gray-300"
                        : ""
                    }`}
                  >
                    <td
                      className={`py-2 px-4 text-left font-extralight bg-white dark:bg-darkSecondaryBackground dark:text-white ${
                        index === 0 ? "rounded-tl-md" : ""
                      } ${
                        index === licenses.length - 1 ? "rounded-bl-md" : ""
                      }`}
                    >
                      {license.name}
                    </td>
                    <td className="py-2 px-4 text-left font-extralight bg-white dark:bg-darkSecondaryBackground dark:text-white">
                      {license.email}
                    </td>
                    <td className="py-2 px-4 text-left font-extralight bg-white dark:bg-darkSecondaryBackground dark:text-white">
                      {license.numberOfAccounts}
                    </td>
                    <td className="py-2 px-4 text-left font-extralight bg-white dark:bg-darkSecondaryBackground dark:text-white">
                      {datetimeFormat(license.createdAt)}
                    </td>
                    <td className="py-2 px-4 text-left font-extralight bg-white dark:bg-darkSecondaryBackground dark:text-white">
                      {datetimeFormat(license.expiredAt)}
                    </td>
                    <td
                      className={`py-2 px-4 text-right flex font-extralight space-x-2 items-center bg-white dark:bg-darkSecondaryBackground dark:text-white ${
                        index === 0 ? "rounded-tr-md" : ""
                      } ${
                        index === licenses.length - 1 ? "rounded-br-md" : ""
                      }`}
                    >
                      <MdEdit
                        className="text-black ml-auto cursor-pointer dark:text-white"
                        size={24}
                        onClick={() => navigate(`/add-license/${license.id}`)}
                      />
                      <FaTrash
                        className="text-red_1 cursor-pointer"
                        size={20}
                        onClick={() => openConfirmationModal(license)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {licenses.length === 0 && (
            <div className="text-center my-4 col-span-full">
              <p className="text-lg text-gray-500">
                No data matches this category.
              </p>
            </div>
          )}
        </div>
        {loading && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
            <CircularProgress />
          </div>
        )}
      </div>
      {licenseToDelete && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={() => handleDeleteLicense(licenseToDelete.id)}
          label={t.deleteLicenseConfirmation.replace(
            "{label}",
            licenseToDelete.name
          )}
        />
      )}
    </div>
  );
};

export default LicensesList;
